/**
 * @file: ChatConversationView.tsx
 *
 * A chat conversation rendered from an already-resolved
 * {@link ChatConversationHandle} — navigation-agnostic, so both the mobile
 * screen and the desktop detail pane mount it directly (no page is ever
 * rendered as a component).
 *
 * It does NOT resolve: the room is resolved at the open gate (the inbox host's
 * spinner, or the mobile screen's entry resolve) BEFORE this mounts, so it
 * takes a concrete handle — the pane never shows a half-resolved room.
 *
 * The host owns the chrome: pass `onOpenRoomInfo` to let the conversation own
 * an in-pane header (the desktop pane); omit it and mount your own title
 * driver as `children` (the mobile screen mounts `<TypingIndicator/>` to drive
 * the navigator title). `onConversationHandleMaterialized` reports the ids back
 * once a first send materialises the room/thread (a `bare` handle → `baked`).
 */

import {
  BakedConversationHandle,
  ChatConversationHandle,
} from '@euler/app/flows/chat/lib/conversation/handle';
import { FC, ReactNode, memo } from 'react';
import { ChatConversationProvider } from '../../lib/conversation/provider';
import { ChatConversation } from './ChatConversation';

export const ChatConversationView: FC<{
  handle: ChatConversationHandle;
  onConversationHandleMaterialized?: (handle: BakedConversationHandle) => void;
  onOpenRoomInfo?: () => void;
  /** Host exit after the viewer deletes a closed thread (removed/dismissed). */
  onExit?: () => void;
  children?: ReactNode;
}> = memo(
  ({
    handle,
    onConversationHandleMaterialized,
    onOpenRoomInfo,
    onExit,
    children,
  }) => {
    return (
      <ChatConversationProvider
        conversationHandle={handle}
        onConversationHandleMaterialized={onConversationHandleMaterialized}
      >
        <ChatConversation onOpenRoomInfo={onOpenRoomInfo} onExit={onExit} />
        {children}
      </ChatConversationProvider>
    );
  },
);
